import { useEffect, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { mediaURL } from 'src/consumer/services'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import { updateSupportPractitionerEnabled } from 'src/supportPortal/services/practitionerOnboardingService'

const TYPE_TABS = [
  { id: 'all', label: 'All' },
  { id: 'therapist', label: 'Therapists' },
  { id: 'doctor', label: 'Doctors' },
  { id: 'screener', label: 'Screeners' },
]

const TYPE_LABEL = {
  therapist: 'Therapist',
  doctor: 'Doctor',
  screener: 'Screener',
}

/**
 * Phase 14i — listing for kyc-approved-but-disabled practitioners. Lands here from the
 * Practitioner Management nav page after an agent uses "Save & Keep Disabled" or pauses an
 * existing practitioner — these rows otherwise vanish from the active therapists/doctors/
 * screeners listings (which filter on enabled=true) and from Applicants (filter:
 * kycStatus=pending).
 *
 * Each row shows the practitioner identity + chips, plus an inline Enable button that flips
 * enabled=true via the existing updatePractitionerEnabled endpoint. A "Review in onboarding"
 * link drops the agent into the same onboarding form they'd use for a fresh save.
 */
export default function DisabledPractitionersPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeType = (searchParams.get('type') || 'all').toLowerCase()
  const [state, setState] = useState({ loading: true, error: '', data: [] })
  const [enablingId, setEnablingId] = useState(null)

  async function load(typeForLoad) {
    setState((prev) => ({ ...prev, loading: true, error: '' }))
    const params = typeForLoad && typeForLoad !== 'all' ? { type: typeForLoad } : {}
    const resp = await getSPDataWithParams(
      '/api/secure/cc/support/practitioner-onboarding/disabled-practitioners',
      params,
    )
    if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
      const list = getDataMapFromCommonResponse(resp)?.practitioners || []
      setState({ loading: false, error: '', data: list })
    } else {
      setState({
        loading: false,
        error: resp?.data?.message || 'Could not load disabled practitioners.',
        data: [],
      })
    }
  }

  useEffect(() => {
    load(activeType)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeType])

  async function handleEnable(row) {
    if (!row?.practitionerId) return
    setEnablingId(row.practitionerId)
    const resp = await updateSupportPractitionerEnabled(
      { practitionerId: row.practitionerId, practitionerSlug: row.practitionerSlug },
      true,
    )
    setEnablingId(null)
    if (resp.ok) {
      toast.success(`${row.name || row.practitionerId} enabled.`)
      load(activeType)
    } else {
      toast.error(resp.message || 'Could not enable practitioner.')
    }
  }

  function renderRow(row) {
    const types = (row.categories || []).filter((c) => TYPE_LABEL[c])
    const typeLabel = types.length ? types.map((c) => TYPE_LABEL[c] || c).join(' · ') : 'Practitioner'
    return (
      <Paper
        key={row.practitionerId || row.userId}
        variant="outlined"
        sx={{ p: 2, borderRadius: 2 }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <Avatar
            src={row.profilePicId ? `${mediaURL}${row.profilePicId}` : undefined}
            alt={row.name || row.practitionerId}
            sx={{ width: 48, height: 48 }}
          >
            {(row.name || '?').charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {row.name || row.practitionerId}
              </Typography>
              <Chip size="small" label="Disabled" color="warning" />
              <Chip size="small" variant="outlined" label={typeLabel} />
              {row.kycStatus && (
                <Chip size="small" variant="outlined" label={`kyc: ${row.kycStatus}`} />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {row.email || row.mobile || 'No contact info'}
            </Typography>
            {row.shortDesc && (
              <Typography variant="body2" sx={{ mt: 0.5 }} color="text.secondary">
                {row.shortDesc}
              </Typography>
            )}
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              {(row.qualifications || []).slice(0, 4).map((q) => (
                <Chip key={`${row.practitionerId}-q-${q}`} size="small" label={q} variant="outlined" />
              ))}
            </Stack>
          </Box>
          <Stack direction="column" spacing={1}>
            <Button
              variant="contained"
              color="success"
              disabled={enablingId === row.practitionerId}
              onClick={() => handleEnable(row)}
            >
              {enablingId === row.practitionerId ? 'Enabling…' : 'Enable now'}
            </Button>
            <Button
              component={RouterLink}
              variant="outlined"
              size="small"
              to={`/support-home/practitioner-management/onboarding?practitioner-id=${
                row.practitionerId
              }&type=${types[0] || 'therapist'}`}
            >
              Review in onboarding
            </Button>
          </Stack>
        </Stack>
      </Paper>
    )
  }

  return (
    <>
      <SupportHeader heading="Disabled Practitioners" showBackButton />
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          KYC-approved practitioners that are currently disabled — typically created via Save &amp;
          Keep Disabled, or paused after onboarding. Re-enable here to put them live; visit
          Onboarding to amend any field first.
        </Typography>

        <Tabs
          value={activeType}
          onChange={(_e, next) => {
            const params = new URLSearchParams(searchParams)
            if (next === 'all') params.delete('type')
            else params.set('type', next)
            setSearchParams(params, { replace: false })
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2 }}
        >
          {TYPE_TABS.map((tab) => (
            <Tab key={tab.id} value={tab.id} label={tab.label} />
          ))}
        </Tabs>

        {state.loading ? (
          <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : state.error ? (
          <Alert severity="error">{state.error}</Alert>
        ) : state.data.length === 0 ? (
          <Alert severity="info">
            No disabled practitioners {activeType === 'all' ? '' : `in the ${TYPE_LABEL[activeType]?.toLowerCase()} bucket`} right now.
          </Alert>
        ) : (
          <Stack spacing={1.5}>{state.data.map(renderRow)}</Stack>
        )}
      </Box>
    </>
  )
}
